import { z } from "zod";
import { useLedgersByEntityIds } from "@api/ledger.api";
import { DEFAULT_TRANSACTION_BULK_UPDATE_CHUNK_SIZE } from "@common/constants/transactions";
import { UpdateTransactionDto } from "@common/dto/transactions/update-transaction.dto";
import { WithRequiredProperties } from "@common/types/base/generics";
import { Transaction } from "@common/types/transaction";
import { TransactionView } from "@common/types/views/transactionViews";
import { useTransactionViewStore } from "@hooks/view/useTransactionViewStore";
import type { PaginationMode } from "@hooks/paginationMode";
import type { QueryKey } from "@tanstack/react-query";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "@hooks/useWorkspaceUserCache";
import { useEntities } from "@hooks/useEntities";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { Loader } from "@design-system/Loader/Loader";
import { useIgnoreLoosingAccessToTransaction } from "@hooks/useIgnoreLoosingAccessToTransaction";
import { keyBy } from "@common/helpers/collections";
import { omit } from "@common/helpers/objects";
import React, {
    startTransition,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    BulkUpdateTransactionsRequestInput,
    TransactionListItem,
} from "@common/schemas/transaction.schema";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import { useSequentialRequest } from "@hooks/useSequentialRequests";
import { useStandardCategories } from "@hooks/useStandardCategories";
import { useTransactionsPageSortingCache } from "@hooks/view/useTransactionsPageSortingCache";
import { bulkUpdateTransactions } from "@api/transaction.api";
import { invalidateCounterpartyQueriesForWorkspace } from "@api/counterparty.api";
import { useMatchingTransactionsForRuleQuery } from "@api/rules.api";
import { uuidv7 } from "uuidv7";
import { getTransactionDetails } from "@lib/transactions";
import { removeJournalEntryQueries } from "@components/accounting/journalEntryDrawer/journalEntryQuery";
import { useUpdateTransactionMutation } from "@mutations/transaction";
import { StandardModal } from "@design-system/Modal/Modal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useScope } from "@state/scopeContext.lib";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { queryClient } from "../../queryClient";
import { ChildrenProps } from "../../types";
import { RuleCreationToast } from "../transactionRules/RuleCreationToast/RuleCreationToast";
import { TransactionsFilters } from "./filters/lib";
import { resolveAccrualMatchListIncludes } from "./resolveAccrualMatchListIncludes";
import { LoosingAccessToTransactionModal } from "./LoosingAccessToTransactionModal";
import {
    invalidateTransactionDetailsQueries,
    mergeTransactionIntoDetailsCache,
} from "./TransactionDetails/useTransactionDetailsQuery";
import { TransactionSortValue, useSort } from "./useSort";
import {
    invalidateTransactionsQueries,
    TRANSACTIONS_PAGE_SIZE,
} from "./useTransactionsQuery";
import { useTransactionsListRows } from "./useTransactionsListRows";

import {
    BulkUpdateDetails,
    BulkUpdateStatus,
    preparePayloadsForUndo,
} from "./TransactionsBulkActions/lib";
import { TOAST_MESSAGE_KEY } from "./TransactionsBulkActions/TransactionsBulkActionsContextProvider.lib";
import { buildBulkUpdateFailureError } from "./TransactionsBulkActions/bulkUpdateFailure";
import { TransactionsUpdateToast } from "./TransactionsBulkActions/TransactionsUpdateStatus/TransactionsUpdateToast";
import { showInheritedRuleToast } from "./rulesSuggestion/showInheritedRuleToast";
import { useRulesSuggestionAvailable } from "./rulesSuggestion/useRulesSuggestionAvailable";
import { checkSimilarTransactionsAndShowToastIfApplicable } from "./similarTransactionsUpdate";
import { SelectedTransactionBase } from "./TransactionsBulkActions/transactionsBulkActionsContext";
import { useTransactionsStatisticsQuery } from "./useTransactionsStatisticsQuery";
import {
    PaginatedTransactionsDataContext,
    PaginatedTransactionsActionsContext,
    PaginatedTransactionsDataContextValue,
    PaginatedTransactionsActionsContextValue,
    MatchingRuleData,
} from "./PaginatedTransactionsTable.context";

// --- Provider ---

/**
 * Numbered pages driven by the caller's page state, or load more on scroll.
 * Matching lists come one numbered page at a time, so only pages mode takes
 * `matchingTransactionId` and `matchingRuleData`.
 */
type PaginationProps =
    | {
          paginationMode: PaginationMode;
          matchingTransactionId?: undefined;
          matchingRuleData?: undefined;
      }
    | {
          paginationMode: Extract<PaginationMode, { type: "pages" }>;
          matchingTransactionId?: number;
          matchingRuleData?: MatchingRuleData;
      };

interface ProviderOptionProps {
    defaultView?: TransactionView;
    cacheKey?: WORKSPACE_USER_CACHE_KEYS;
    initialFilters?: TransactionsFilters;
    initialView?: TransactionView;
    initialBaseViewId?: string;
    parentFilters?: TransactionsFilters;
    onInitialFiltersConsumed?: () => void;
    disableSimilarTransactionsUpdate?: boolean;
    resetFiltersOnMount?: boolean;
    includeMatchDetails?: boolean;
    includeAccrualMatchDetails?: boolean;
    queryKeyPrefix?: string;
}

type PaginatedTransactionsTableContextProviderProps = ChildrenProps &
    PaginationProps &
    ProviderOptionProps;

interface LoosingAccessContext {
    ownerName: string;
    transaction: TransactionListItem;
    payload: UpdateTransactionDto;
}

interface CachedTransactionsPage {
    data: TransactionListItem[];
    total?: number;
    pageCount?: number;
}

/**
 * Rewrites the rows of every cached list entry in `queryKeys` (a page, an id
 * snapshot or a snapshot batch), keeping the entry's other fields. An entry
 * with no rows cached becomes `emptyFallback`, or stays untouched without one.
 */
function patchCachedRows({
    queryKeys,
    mapRows,
    emptyFallback,
}: {
    queryKeys: QueryKey[];
    mapRows: (rows: TransactionListItem[]) => TransactionListItem[];
    emptyFallback?: CachedTransactionsPage;
}) {
    for (const queryKey of queryKeys) {
        queryClient.setQueryData(
            queryKey,
            (prev: CachedTransactionsPage | undefined) =>
                prev?.data
                    ? { ...prev, data: mapRows(prev.data) }
                    : emptyFallback,
        );
    }
}

const PaginatedTransactionsTableContextProviderInner: React.FC<
    PaginatedTransactionsTableContextProviderProps
> = ({
    paginationMode,
    children,
    cacheKey,
    initialFilters,
    initialView,
    initialBaseViewId,
    parentFilters,
    onInitialFiltersConsumed,
    defaultView,
    matchingTransactionId,
    matchingRuleData,
    disableSimilarTransactionsUpdate,
    resetFiltersOnMount,
    includeMatchDetails,
    includeAccrualMatchDetails,
    queryKeyPrefix,
}) => {
    const [loosingAccessContext, setLoosingAccessContext] =
        useState<LoosingAccessContext>();
    const { ignoreLoosingAccess } = useIgnoreLoosingAccessToTransaction();
    const { disableAiRules } = useTypedFlags();
    const { enabled: isGlFirst } = useIsGlFirst();
    const isSimilarTransactionsUpdateDisabled =
        disableSimilarTransactionsUpdate || disableAiRules;
    const [shownTransactionId, setShownTransactionId] = useState<number>();
    const availableEntities = useEntities();
    const availableEntityIds = availableEntities.map((entity) => entity.id);
    const { toast, dismiss } = useToaster();
    const isRulesSuggestionAvailable = useRulesSuggestionAvailable();
    const [cachedRulesSuggestionEnabled, setCachedRulesSuggestionEnabled] =
        useWorkspaceUserCache(
            WORKSPACE_USER_CACHE_KEYS.RULES_SUGGESTION_ENABLED,
            z.boolean(),
        );
    const [isRulesSuggestionToggledOn, setIsRulesSuggestionToggledOn] =
        useState(cachedRulesSuggestionEnabled ?? false);
    const isRulesSuggestionEnabled =
        isRulesSuggestionAvailable && isRulesSuggestionToggledOn;
    const setRulesSuggestionEnabled = useCallback(
        (isEnabled: boolean) => {
            setIsRulesSuggestionToggledOn(isEnabled);
            setCachedRulesSuggestionEnabled(isEnabled);
        },
        [setCachedRulesSuggestionEnabled],
    );
    const { data: ruleSuggestionLedgers } = useLedgersByEntityIds(
        isRulesSuggestionEnabled ? availableEntityIds : [],
    );
    const { isViewOnly } = useViewOnlyRefusal();

    // Confirmed document matches live on accrual_match, including cash
    // approvals, so the match column and category button need them on every
    // list. Invoice and bill suggestions use the same payload — cash-basis
    // workspaces still generate them and the Match cell reads them from
    // `accrualMatchSuggestions`.
    const {
        includeAccrualMatchDetails: resolvedIncludeAccrualMatchDetails,
        includeAccrualMatchSuggestions: resolvedIncludeAccrualMatchSuggestions,
    } = resolveAccrualMatchListIncludes(includeAccrualMatchDetails);

    const { view: currentView, setView: setCurrentView } =
        useTransactionViewStore({
            defaultView,
            initialView,
            initialFilters,
            parentFilters,
            cacheKey,
            onInitialFiltersConsumed,
            resetFiltersOnMount,
        });

    const [cachedSorting, setCachedSorting] = useTransactionsPageSortingCache();
    const initialSort = cachedSorting ?? TransactionSortValue.DATE_DESC;
    const { currentSort, sortExpression, handleSortChange } =
        useSort(initialSort);

    // Tracks whether the in-flight query refetch was triggered by a sort
    // change, as opposed to pagination, filter changes, or background
    // refetches. Cleared once the triggered fetch resolves.
    const [isSortRefetching, setIsSortRefetching] = useState(false);

    const setCurrentFilters = useCallback(
        (filters: TransactionsFilters) => {
            setCurrentView((prev) => ({ ...prev, filters }));
        },
        [setCurrentView],
    );

    const currentFilters = currentView.filters as WithRequiredProperties<
        TransactionsFilters,
        "entityIds"
    >;

    const [isMatchingRuleMode, setIsMatchingRuleMode] = useState(
        matchingRuleData?.defaultToMatchingMode ?? false,
    );
    const isMatchingMode = !!matchingTransactionId;

    const { data: statistics } = useTransactionsStatisticsQuery({
        filters: currentFilters,
        enabled: !isMatchingMode && !isMatchingRuleMode,
    });

    const statisticsCount = statistics?.count;

    const {
        page,
        setPage,
        data,
        isFetching: isTransactionsFetching,
        cacheKeys,
        loadMore,
    } = useTransactionsListRows({
        paginationMode,
        filters: currentFilters,
        sort: sortExpression,
        matchingTransactionId,
        includeMatchDetails,
        includeAccrualMatchDetails: resolvedIncludeAccrualMatchDetails,
        includeAccrualMatchSuggestions: resolvedIncludeAccrualMatchSuggestions,
        queryKeyPrefix,
    });

    const handleSortChangeWithPageReset = useCallback(
        (value: TransactionSortValue) => {
            setIsSortRefetching(true);
            startTransition(() => {
                handleSortChange(value);
                setCachedSorting(value);
                setPage(1);
            });
        },
        [handleSortChange, setCachedSorting, setPage],
    );

    const { workspaceId: scopeWorkspaceId } = useScope();
    const matchingRulePage = isMatchingRuleMode ? page - 1 : 0;
    const matchingRuleQuery = useMatchingTransactionsForRuleQuery({
        ruleId: matchingRuleData?.ruleId,
        conditions: matchingRuleData?.conditions,
        groupType: matchingRuleData?.groupType,
        page: matchingRulePage,
        limit: TRANSACTIONS_PAGE_SIZE,
        enabled: !!matchingRuleData,
    });
    const matchingRuleTotal = matchingRuleQuery.data?.total;

    const activeData = isMatchingRuleMode ? matchingRuleQuery.data : data;
    const activeIsFetching = isMatchingRuleMode
        ? matchingRuleQuery.isFetching
        : isTransactionsFetching;

    useEffect(() => {
        if (!activeIsFetching) {
            setIsSortRefetching(false);
        }
    }, [activeIsFetching]);

    useEffect(() => {
        const count =
            isMatchingRuleMode || isMatchingMode
                ? activeData?.total
                : statisticsCount;

        if (count === undefined) {
            return;
        }

        const totalPages = Math.max(
            1,
            Math.ceil(count / TRANSACTIONS_PAGE_SIZE),
        );

        if (totalPages < page) {
            setPage(totalPages);
        }
    }, [
        isMatchingMode,
        isMatchingRuleMode,
        statisticsCount,
        activeData?.total,
        page,
        setPage,
    ]);

    const updateTransaction = useUpdateTransactionMutation();

    // Ref-ify the keys so upsertTransaction/hideTransaction/updateMany are
    // stable. One key per page shown; load more holds one per loaded batch.
    const cacheKeysRef = useLatestRef(cacheKeys);

    const upsertTransaction = useCallback(
        (transaction: TransactionListItem) => {
            // Every route into these writers is a write the API will refuse
            // for a View-only user, so writing the row first only shows the
            // new value for as long as the request takes to fail. Skipping the
            // write also makes the rollback in `saveTransaction` a no-op. The
            // refusal itself stays the server's 403 toast.
            if (isViewOnly) {
                return;
            }

            patchCachedRows({
                queryKeys: cacheKeysRef.current,
                mapRows: (rows) =>
                    rows.map((t) =>
                        t.id === transaction.id ? transaction : t,
                    ),
                emptyFallback: {
                    data: [transaction],
                    total: 1,
                    pageCount: 1,
                },
            });
        },
        [isViewOnly, cacheKeysRef],
    );

    const hideTransaction = useCallback(
        (transaction: TransactionListItem) => {
            // Same reason as `upsertTransaction` above.
            if (isViewOnly) {
                return;
            }

            patchCachedRows({
                queryKeys: cacheKeysRef.current,
                mapRows: (rows) => rows.filter((t) => t.id !== transaction.id),
                emptyFallback: { data: [], total: 0, pageCount: 0 },
            });
        },
        [isViewOnly, cacheKeysRef],
    );

    const updateMany = useCallback(
        (transactionsToUpdate: TransactionListItem[]) => {
            // Same reason as `upsertTransaction` above.
            if (isViewOnly) {
                return;
            }

            const dict = keyBy(transactionsToUpdate, "id");
            patchCachedRows({
                queryKeys: cacheKeysRef.current,
                mapRows: (rows) =>
                    rows.map((t) => (dict[t.id] ? { ...t, ...dict[t.id] } : t)),
            });
        },
        [isViewOnly, cacheKeysRef],
    );

    const activeWorkspaceId = scopeWorkspaceId;

    const [updateDetails, setUpdateDetails] =
        useState<BulkUpdateDetails | null>(null);

    const updateSequence = useSequentialRequest(
        bulkUpdateTransactions.bind(null, activeWorkspaceId ?? ""),
    );

    // Declared before doUpdate so the ref is available in its closure.
    const updateSequenceRef = useRef(updateSequence);
    updateSequenceRef.current = updateSequence;

    const dismissRef = useRef(dismiss);
    dismissRef.current = dismiss;

    const activeWorkspaceIdRef = useRef(activeWorkspaceId);
    activeWorkspaceIdRef.current = activeWorkspaceId;

    const doUpdate = useCallback(
        async (
            details: BulkUpdateDetails,
            payloads: BulkUpdateTransactionsRequestInput[],
            onUpdated?: (
                result: TransactionListItem[],
                details: BulkUpdateDetails,
                previousTransactions: Record<number, Transaction>,
            ) => void,
        ) => {
            setUpdateDetails(details);

            const processId = uuidv7();
            const payloadsWithProcessId = payloads.map((p) => ({
                ...p,
                processId,
            }));

            let completedSucceededCount = 0;
            let completedRequestCount = 0;
            let result: Array<
                Awaited<ReturnType<typeof bulkUpdateTransactions>>
            >;
            try {
                result = (
                    await updateSequenceRef.current.start(
                        payloadsWithProcessId,
                        ({ updatedTransactions }) => {
                            completedSucceededCount +=
                                updatedTransactions.length;
                            completedRequestCount += 1;
                        },
                    )
                ).flat();
            } catch (error) {
                dismissRef.current(TOAST_MESSAGE_KEY);
                invalidateTransactionsQueries();
                removeJournalEntryQueries();
                throw buildBulkUpdateFailureError({
                    cause: error instanceof Error ? error : undefined,
                    completedSucceededCount,
                    unsentRequestCount:
                        payloads.length - completedRequestCount - 1,
                });
            }

            const updatedTransactions = result
                .map((r) => r.updatedTransactions)
                .flat();
            const transactionsBeforeUpdate = result
                .map((r) => r.transactionsBeforeUpdate)
                .flat();

            const allCanonicalIds = result
                .map((r) => r.canonicalNormalizedDescriptionId)
                .filter(Boolean) as string[];
            const uniqueCanonicalIds = [...new Set(allCanonicalIds)];
            const canonicalNormalizedDescriptionId =
                uniqueCanonicalIds.length === 1 ? uniqueCanonicalIds[0] : null;

            onUpdated?.(updatedTransactions, details, transactionsBeforeUpdate);

            setUpdateDetails((prev) => ({
                ...prev!,
                status: BulkUpdateStatus.SUCCESS,
            }));
            invalidateTransactionsQueries();
            removeJournalEntryQueries();

            // The 1099-excluded amounts shown in the counterparty directory
            // (e.g. the Form 1099 close task) are derived from journal-entry
            // lines and don't get refreshed by invalidateTransactionsQueries,
            // so they'd otherwise keep showing stale totals until a manual
            // page refresh.
            if (
                details.update === "excludeFrom1099" &&
                activeWorkspaceIdRef.current
            ) {
                void invalidateCounterpartyQueriesForWorkspace(
                    activeWorkspaceIdRef.current,
                );
            }

            return {
                updatedTransactions,
                details,
                transactionsBeforeUpdate,
                canonicalNormalizedDescriptionId,
            };
        },
        [],
    );

    const standardCategories = useStandardCategories();

    // Refs to stabilize action callbacks — avoids recreating doUpdate/undo/saveTransaction
    // when query results or other non-setter values change. This keeps the actions
    // context reference stable across data-loading cascades.
    const currentViewRef = useRef(currentView);
    currentViewRef.current = currentView;

    const availableEntityIdsRef = useRef(availableEntityIds);
    availableEntityIdsRef.current = availableEntityIds;

    const availableEntitiesRef = useRef(availableEntities);
    availableEntitiesRef.current = availableEntities;

    const standardCategoriesRef = useRef(standardCategories);
    standardCategoriesRef.current = standardCategories;

    const ignoreLoosingAccessRef = useRef(ignoreLoosingAccess);
    ignoreLoosingAccessRef.current = ignoreLoosingAccess;

    const updateTransactionRef = useRef(updateTransaction);
    updateTransactionRef.current = updateTransaction;

    const disableSimilarTransactionsUpdateRef = useRef(
        isSimilarTransactionsUpdateDisabled,
    );
    disableSimilarTransactionsUpdateRef.current =
        isSimilarTransactionsUpdateDisabled;

    const toastRef = useRef(toast);
    toastRef.current = toast;

    const upsertTransactionRef = useRef(upsertTransaction);
    upsertTransactionRef.current = upsertTransaction;

    const hideTransactionRef = useRef(hideTransaction);
    hideTransactionRef.current = hideTransaction;

    const undo = useCallback(
        async ({
            previousTransactions,
            updateDetailsForUndo,
        }: {
            previousTransactions: Record<number, SelectedTransactionBase>;
            updateDetailsForUndo: BulkUpdateDetails;
        }) => {
            const payloads = preparePayloadsForUndo({
                lastUpdateParams: updateDetailsForUndo,
                previousTransactions,
                standardCategories: standardCategoriesRef.current,
            });

            try {
                const newDetails = {
                    ...updateDetailsForUndo,
                    status: BulkUpdateStatus.UPDATING,
                    transactionsCount: Object.keys(previousTransactions).length,
                    isUndo: true,
                };
                await doUpdate(newDetails, payloads);

                toastRef.current(
                    <TransactionsUpdateToast
                        updateDetails={{
                            ...newDetails,
                            status: BulkUpdateStatus.SUCCESS,
                        }}
                    />,
                    TOAST_MESSAGE_KEY,
                );
            } catch {
                setUpdateDetails((prev) => ({
                    ...prev!,
                    status: BulkUpdateStatus.ERROR,
                }));
            }
        },
        [doUpdate],
    );

    useEffect(() => {
        if (
            updateSequence.total > 1 &&
            updateSequence.progress < updateSequence.total &&
            updateDetails
        ) {
            toast(
                <TransactionsUpdateToast
                    updateDetails={updateDetails}
                    updateProgress={
                        updateSequence.progress *
                        DEFAULT_TRANSACTION_BULK_UPDATE_CHUNK_SIZE
                    }
                />,
                TOAST_MESSAGE_KEY,
                { preventAutoClose: true },
            );
        }
    }, [updateSequence.progress, updateSequence.total, updateDetails, toast]);

    const saveTransaction = useCallback(
        async (
            transaction: TransactionListItem,
            payload: UpdateTransactionDto,
            displayedChangedEntityName?: string,
        ) => {
            try {
                // The optimistic-update path merges the PATCH payload
                // into the cached row. `accountOverrides` uses a
                // nullable-accountId request shape that isn't directly
                // assignable to the response shape, and per-ledger
                // entries don't merge cleanly with a flat spread —
                // drop them from the optimistic merge and let the
                // server response repopulate them on success.
                const updateForList = omit(
                    payload,
                    "taxQuestionAnswers",
                    "accountOverrides",
                );
                if (!payload.entityId && !payload.categoryId) {
                    upsertTransactionRef.current({
                        ...transaction,
                        ...updateForList,
                    });
                }

                const response = await updateTransactionRef.current.mutateAsync(
                    {
                        transaction,
                        update: {
                            loosingAccessConfirmed:
                                ignoreLoosingAccessRef.current,
                            ...payload,
                        },
                    },
                );

                if ("transaction" in response) {
                    const updated = response.transaction;

                    // Same rationale as in doUpdate: counterparty directory
                    // totals (e.g. the Form 1099 close task) are derived from
                    // journal-entry lines and don't refresh automatically
                    // when a single transaction's 1099 exclusion changes.
                    if (
                        payload.excludeFrom1099 !== undefined &&
                        activeWorkspaceIdRef.current
                    ) {
                        void invalidateCounterpartyQueriesForWorkspace(
                            activeWorkspaceIdRef.current,
                        );
                    }

                    let similarTransactionsWereChecked = false;
                    if (
                        !disableSimilarTransactionsUpdateRef.current &&
                        response.transaction.normalizedDescriptionId
                    ) {
                        // Outcome-based: only suppress the create-rule toast
                        // when the similar-tx check actually showed its toast.
                        // The two are mutually exclusive, so we must await it.
                        // This is an advisory lookup — a failure here must not
                        // roll back the already-persisted mutation, so swallow
                        // errors and default to "not checked".
                        try {
                            similarTransactionsWereChecked =
                                await checkSimilarTransactionsAndShowToastIfApplicable(
                                    {
                                        response,
                                        payload,
                                        transactionBeforeUpdate: transaction,
                                        toast: toastRef.current,
                                        doUpdate,
                                        undo,
                                        displayedChangedEntityName,
                                        view: currentViewRef.current,
                                        workspaceId:
                                            activeWorkspaceIdRef.current ?? "",
                                    },
                                );
                        } catch {
                            similarTransactionsWereChecked = false;
                        }
                    }

                    const didShowInheritedRuleToast =
                        isRulesSuggestionEnabled &&
                        !disableSimilarTransactionsUpdateRef.current &&
                        !similarTransactionsWereChecked &&
                        showInheritedRuleToast({
                            payload,
                            transaction: {
                                entityId: transaction.entity.id,
                                counterpartyId: transaction.counterpartyId,
                                bankDescription: transaction.bankDescription,
                                categoryId: transaction.categoryId,
                                categoryTaxonomy:
                                    transaction.category?.taxonomy,
                                isGlFirst,
                                ledgers:
                                    ruleSuggestionLedgers?.[
                                        response.transaction.entityId
                                    ],
                                ruleAppliedActionTypes:
                                    response.ruleAppliedActionTypes,
                                previousClassIds:
                                    transaction.classAssignments?.map(
                                        ({ classId }) => classId,
                                    ) ?? [],
                            },
                            toast: toastRef.current,
                        });

                    if (
                        !similarTransactionsWereChecked &&
                        !didShowInheritedRuleToast &&
                        response.ruleCreationContext
                    ) {
                        toastRef.current(
                            <RuleCreationToast
                                canCreateWithtoutReview
                                displayedChangedEntityName={
                                    displayedChangedEntityName
                                }
                                {...response.ruleCreationContext}
                            />,
                        );
                    }

                    if (response.consolidationParentTransitWarning) {
                        const parentEntity = availableEntitiesRef.current.find(
                            (entity) =>
                                entity.uuid ===
                                response.consolidationParentTransitWarning
                                    ?.parentEntityUuid,
                        );
                        toastRef.current(
                            <span>
                                This entity is a subsidiary in a consolidation
                                group. Owner contributions should route through{" "}
                                {parentEntity?.name ? (
                                    <strong>{parentEntity.name}</strong>
                                ) : (
                                    "the parent entity"
                                )}{" "}
                                rather than being recorded directly on the
                                subsidiary.
                            </span>,
                        );
                    }

                    if (
                        response.counterpartyChangedContext
                            ?.predictedVerifiedCounterpartyChanged &&
                        response.counterpartyChangedContext.counterpartyName
                    ) {
                        toastRef.current(
                            <span>
                                Counterparty set to{" "}
                                <strong>
                                    {
                                        response.counterpartyChangedContext
                                            .counterpartyName
                                    }
                                </strong>
                                . Kick will review this update to improve
                                accuracy.
                            </span>,
                        );
                    }

                    if (
                        !availableEntityIdsRef.current.includes(
                            updated.entity.id,
                        )
                    ) {
                        // user no longer has access to the transaction
                        hideTransactionRef.current(updated);
                        setShownTransactionId(undefined);

                        return updated;
                    } else {
                        // Class-only edits produce exactly one request (the
                        // PATCH). The response already carries the updated
                        // transaction with classAssignments, so upsert it into
                        // the list cache directly and merge it into any open
                        // details entry — no follow-up GET, no invalidation.
                        const isClassOnlyUpdate = Object.keys(payload).every(
                            (key) => key === "classIds",
                        );
                        if (isClassOnlyUpdate) {
                            // The PATCH response carries no schedule badge or
                            // counterparty/customer relations; keep the cached
                            // ones so the schedule lock and the counterparty
                            // and customer names survive a class-only save.
                            // Return the merged row too: queued class saves
                            // use it as the base for the next save.
                            const mergedRow: TransactionListItem = {
                                ...updated,
                                schedule: transaction.schedule,
                                matchedSchedules: transaction.matchedSchedules,
                                counterparty:
                                    updated.counterpartyId ===
                                    transaction.counterpartyId
                                        ? transaction.counterparty
                                        : updated.counterparty,
                                customer:
                                    updated.customerId ===
                                    transaction.customerId
                                        ? transaction.customer
                                        : updated.customer,
                            };
                            upsertTransactionRef.current(mergedRow);
                            mergeTransactionIntoDetailsCache(updated);
                            return mergedRow;
                        }

                        // refresh transaction if user still has access to it
                        await invalidateTransactionDetailsQueries();

                        const updatedTransaction = await getTransactionDetails(
                            transaction.id,
                        );

                        // The details endpoint doesn't carry accrual match
                        // suggestions; a memo can't change them, so keep the
                        // row's current ones (read now, not from the
                        // pre-save copy, since a Confirm may have landed
                        // mid-save) instead of dropping the row's Confirm.
                        const isMemoOnlyUpdate = Object.keys(payload).every(
                            (key) => key === "memo",
                        );
                        const cachedRow = cacheKeysRef.current
                            .flatMap(
                                (queryKey) =>
                                    queryClient.getQueryData<{
                                        data: TransactionListItem[];
                                    }>(queryKey)?.data ?? [],
                            )
                            .find(({ id }) => id === transaction.id);
                        upsertTransactionRef.current(
                            isMemoOnlyUpdate
                                ? {
                                      ...updatedTransaction.transaction,
                                      accrualMatchSuggestions: (
                                          cachedRow ?? transaction
                                      ).accrualMatchSuggestions,
                                  }
                                : updatedTransaction.transaction,
                        );

                        return updatedTransaction.transaction;
                    }
                } else {
                    setLoosingAccessContext({
                        ownerName: response.loosingAccessTo,
                        transaction,
                        payload,
                    });
                }
            } catch (e) {
                upsertTransactionRef.current(transaction);
                throw e;
            }
        },
        [
            doUpdate,
            undo,
            isRulesSuggestionEnabled,
            ruleSuggestionLedgers,
            isGlFirst,
            cacheKeysRef,
        ],
    );

    const onLoosingAccessModalClosed = useCallback(
        (confirmLoosingAccess?: boolean) => {
            if (confirmLoosingAccess && loosingAccessContext) {
                saveTransaction(loosingAccessContext.transaction, {
                    ...loosingAccessContext.payload,
                    loosingAccessConfirmed: true,
                }).catch((e) => {
                    throw e;
                });
            }

            setLoosingAccessContext(undefined);
        },
        [loosingAccessContext, saveTransaction],
    );

    // --- Split context values ---

    const dataContextValue =
        useMemo<PaginatedTransactionsDataContextValue>(() => {
            const total =
                isMatchingRuleMode || isMatchingMode
                    ? (activeData?.total ?? activeData?.data?.length ?? 0)
                    : (statisticsCount ??
                      activeData?.total ??
                      activeData?.data?.length ??
                      0);
            const localPageCount = loadMore
                ? 1
                : Math.max(1, Math.ceil(total / TRANSACTIONS_PAGE_SIZE));

            return {
                loadMore,
                currentPage: page,
                totalPages: localPageCount,
                transactionCount: total,
                isTransactionsFetching: isSortRefetching,
                currentSort,
                filters: currentView.filters as WithRequiredProperties<
                    TransactionsFilters,
                    "entityIds"
                >,
                transactions: activeData?.data,
                shownTransactionId,
                currentView,
                initialBaseViewId,
                updateDetails,
                isMatchingRuleMode,
                matchingRuleTotal,
                disableSimilarTransactionsUpdate:
                    isSimilarTransactionsUpdateDisabled,
                isRulesSuggestionEnabled,
            };
        }, [
            loadMore,
            currentSort,
            page,
            activeData,
            statisticsCount,
            isMatchingMode,
            isMatchingRuleMode,
            isSortRefetching,
            shownTransactionId,
            currentView,
            initialBaseViewId,
            updateDetails,
            matchingRuleTotal,
            isSimilarTransactionsUpdateDisabled,
            isRulesSuggestionEnabled,
        ]);

    const actionsContextValue =
        useMemo<PaginatedTransactionsActionsContextValue>(
            () => ({
                setPage,
                handleSortChange: handleSortChangeWithPageReset,
                setCurrentFilters,
                saveTransaction,
                upsertTransaction,
                updateMany,
                setShownTransactionId,
                setCurrentView,
                doUpdate,
                undo,
                setUpdateDetails,
                setIsMatchingRuleMode,
                setRulesSuggestionEnabled,
            }),
            [
                setPage,
                handleSortChangeWithPageReset,
                setCurrentFilters,
                saveTransaction,
                upsertTransaction,
                updateMany,
                setShownTransactionId,
                setCurrentView,
                doUpdate,
                undo,
                setUpdateDetails,
                setIsMatchingRuleMode,
                setRulesSuggestionEnabled,
            ],
        );

    return (
        <>
            <PaginatedTransactionsDataContext.Provider value={dataContextValue}>
                <PaginatedTransactionsActionsContext.Provider
                    value={actionsContextValue}
                >
                    {children}
                </PaginatedTransactionsActionsContext.Provider>
            </PaginatedTransactionsDataContext.Provider>
            <StandardModal
                show={!!loosingAccessContext}
                onHide={onLoosingAccessModalClosed}
            >
                {loosingAccessContext && (
                    <LoosingAccessToTransactionModal
                        ownerName={loosingAccessContext.ownerName}
                        close={onLoosingAccessModalClosed}
                    />
                )}
            </StandardModal>
        </>
    );
};

export const PaginatedTransactionsTableContextProvider: React.FC<
    PaginatedTransactionsTableContextProviderProps
> = (props) => {
    const { initializing } = useIsGlFirst();

    // Defer mounting the store until the active workspace is resolved. The view
    // store's lazy initializer reads the GL-first state once on mount and
    // persists the resulting columns to cache, so mounting while `initializing`
    // is true would bake in (and persist) the optimistic GL-first default for a
    // non-GL-first workspace. See useIsGlFirst / useTransactionViewStore.
    if (initializing) {
        return <Loader />;
    }

    return <PaginatedTransactionsTableContextProviderInner {...props} />;
};
